import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { toast } from "sonner";
import { authLogin, authMe, authLogout, getAdminToken } from "../../lib/adminApi";
import Dashboard from "./Dashboard";
import ListingsIndex from "./ListingsIndex";
import Wizard from "./Wizard";
import Preview from "./Preview";
import TrashView from "./TrashView";
import HistoryView from "./HistoryView";

/**
 * Publisher entry. Gates every /admin/* route behind owner authentication.
 * Production: email + password (HttpOnly cookie session).
 * Preview / Dev fallback: legacy ADMIN_TOKEN bearer (kept in localStorage `wr_admin_token`).
 * Whichever succeeds, the same Publisher shell renders.
 */
export default function AdminDashboard() {
  const [state, setState] = useState("checking"); // 'checking' | 'in' | 'out'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // On mount: ask the server if we already have a session cookie (or a legacy bearer).
  useEffect(() => {
    (async () => {
      try {
        await authMe();
        setState("in");
      } catch {
        setState(getAdminToken() ? "in" : "out"); // legacy bearer fallback
      }
    })();
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authLogin(email, password);
      toast.success("Signed in");
      setState("in");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return <div className="max-w-md mx-auto px-6 py-24 text-[var(--wr-ink-2)]" data-testid="admin-checking">Loading…</div>;
  }

  if (state === "out") {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">/ PUBLISHER</p>
        <h1 className="font-editorial text-5xl leading-tight tracking-tight mb-2">Owner sign-in</h1>
        <p className="text-[14px] text-[var(--wr-ink-2)] mb-8">Sign in with your email and password to open the Publisher.</p>
        <form onSubmit={doLogin} className="space-y-4" autoComplete="on">
          <label className="block">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Email</span>
            <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
              className="mt-2 w-full border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-3 focus:outline-none focus:border-[var(--wr-ink)]"
              data-testid="admin-email-input" autoComplete="username" required autoFocus />
          </label>
          <label className="block">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Password</span>
            <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
              className="mt-2 w-full border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-3 focus:outline-none focus:border-[var(--wr-ink)]"
              data-testid="admin-password-input" autoComplete="current-password" required />
          </label>
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[11px] uppercase tracking-widest hover:bg-[var(--wr-red)] transition-colors disabled:opacity-40"
            data-testid="admin-login-submit">
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <Routes>
      <Route index element={<Dashboard onLogout={async ()=>{ await authLogout(); setState("out"); }} />} />
      <Route path="listings" element={<ListingsIndex />} />
      <Route path="new" element={<Wizard />} />
      <Route path="edit/:id" element={<Wizard />} />
      <Route path="preview/:id" element={<Preview />} />
      <Route path="trash" element={<TrashView />} />
      <Route path="history/:id" element={<HistoryView />} />
    </Routes>
  );
}
